import {
  AddOutlined,
  RemoveOutlined,
  ShoppingCart,
  ShoppingCartOutlined,
} from "@mui/icons-material";
import { Button, IconButton, Stack, Typography, Grid } from "@mui/material";
import { Box } from "@mui/system";
import React from "react";
import { useHistory } from "react-router-dom";
import "./Cart.css";

// Definition of Data Structures used
/**
 * @typedef {Object} Product - Data on product available to buy
 * 
 * @property {string} name - The name or title of the product
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} _id - Unique ID for the product
 */

/**
 * @typedef {Object} CartItem -  - Data on product added to cart
 * 
 * @property {string} name - The name or title of the product in cart
 * @property {string} qty - The quantity of product added to cart
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} productId - Unique ID for the product
 */
 
/**
 * Returns the complete data on all products in cartData by searching in productsData
 *
 * @param { Array.<{ productId: String, qty: Number }> } cartData
 *    Array of objects with productId and quantity of products in cart
 * 
 * @param { Array.<Product> } productsData
 *    Array of objects with complete data on all available products
 *
 * @returns { Array.<CartItem> }
 *    Array of objects with complete data on products in cart
 *
 */
export const generateCartItemsFrom = (cartData = [], productsData = []) => {
  let hm = {};
  // console.log(productsData)
  // console.log(cartData)
  productsData.forEach((ele, index) => {
    hm[ele._id] = index 
  }) 

  // console.log(hm)
  // console.log(cartData)

  if(cartData.length !== 0)
    cartData.forEach((ele, ind) => {
      let detail = hm[ele.productId];
      detail = productsData[detail]
      //console.log(detail)
      ele.cost = detail.cost
      ele["img"] = detail.image
      ele.name = detail.name
      ele.productId = detail._id
      ele.category=detail.category
      ele.rating = detail.rating

    // console.log(ele)
      cartData[ind] = ele
    })

  return cartData
};

/**
 * Get the total value of all products added to the cart
 *
 * @param { Array.<CartItem> } items
 *    Array of objects with complete data on products added to the cart
 *
 * @returns { Number }
 *    Value of all items in the cart
 *
 */
export const getTotalCartValue = (items = []) => {
  let total = 0;
  //console.log("I have been called getTotalCartValue")
  items.forEach((ele) => {
    total += ele.cost * ele.qty
  })

  return total
};


/**
 * Component to display the current quantity for a product and + and - buttons to update product quantity on cart
 * 
 * @param {Number} value
 *    Current quantity of product in cart
 * 
 * @param {Function} handleAdd
 *    Handler function which adds 1 more of a product to cart
 * 
 * @param {Function} handleDelete
 *    Handler function which reduces the quantity of a product in cart by 1
 * 
 * 
 */

/**
 * Component to display the Cart view
 * 
 * @param { Array.<Product> } products
 *    Array of objects with complete data of all available products
 * 
 * @param { Array.<Product> } items
 *    Array of objects with complete data on products in cart
 * 
 * @param {Function} handleDelete
 *    Current quantity of product in cart
 * 
 * 
 */

 

const Cart = ({
  products,
  items = [],
  handleQuantity,
  total,
  isReadOnly
  
    
}) => {
  const ItemQuantity = ({
    value,
    handleAdd,
    handleDelete,
    id
  }) => {
    return (
      <Stack direction="row" alignItems="center">
        {!isReadOnly && <IconButton size="small" color="primary" onClick={async () => {
          await handleDelete(localStorage.getItem("token"), items, products, id, value-1)}}>
          <RemoveOutlined />
        </IconButton>}
        <Box padding="0.5rem" data-testid="item-qty" id="update-value">
          {isReadOnly && "Qty:"}{value}
        </Box>
        {!isReadOnly && <IconButton size="small" color="primary" onClick={async () => {
          await handleAdd(localStorage.getItem("token"), items, products, id, value+1)}}>
          <AddOutlined />
        </IconButton>}
      </Stack>
    );
  };
  
  const history = useHistory();
  // useEffect(() => {
  //   console.log(items)
  // }, [items])
  if (!items.length) {
    return (
      <Box className="cart empty">
        <ShoppingCartOutlined className="empty-cart-icon" />
        <Box color="#aaa" textAlign="center">
          Cart is empty. Add more items to the cart to checkout.
        </Box>
      </Box>
    );
  }

  const CartItem = ({ name, qty, category, cost, rating, image, productId }) => {
    //console.log(image)
    return <Box display="flex" alignItems="flex-start" padding="1rem">
        <Box className="image-container">
            <img
                // Add product image
                src={image}
                // Add product name as alt eext
                alt={name}
                width="100%"
                height="100%"
            />
        </Box>
        <Box
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            height="6rem"
            paddingX="1rem"
        >
            <div>{name}</div>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
            >
            <ItemQuantity
            // Add required props by checking implementation
              value={qty}
              id={productId}
              handleAdd = {handleQuantity}
              handleDelete = {handleQuantity}
            />
            <Box padding="0.5rem" fontWeight="700">
                ${cost}
            </Box>
            </Box>
        </Box>
    </Box>
    }

    const CartDetails = () => {
      return (
        <Box color="#3C3C3C">
          <Stack spacing={2}>
            <Typography variant="h6">Order Details</Typography>
            <Box sx={{display:'flex', flexDirection:'row', justifyContent:"space-between"}}>
              <Typography variant='p'>Products</Typography>
              <Typography variant='p'>{items.length}</Typography>
            </Box>
            <Box sx={{display:'flex', flexDirection:'row', justifyContent:"space-between"}}>
              <Typography variant='p'>SubTotal</Typography>
              <Typography variant='p'>${total}</Typography>
            </Box>
            <Box sx={{display:'flex', flexDirection:'row', justifyContent:"space-between"}}>
              <Typography variant='p'>Shipping Charges</Typography>
              <Typography variant='p'>$0</Typography>
            </Box>
            <Box sx={{display:'flex', flexDirection:'row', justifyContent:"space-between"}}>
              <Typography variant='p'>Total</Typography>
              <Typography variant='p'>${total}</Typography>
            </Box>
          </Stack>
        </Box>
      )
    }
  return (
    <>
      <Box className="cart">
        {/* TODO: CRIO_TASK_MODULE_CART - Display view for each cart item with non-zero quantity */}
        {
          items.map((ele, ind) => {
            // console.log(ele)
            // console.log(ele.img)
            return <CartItem key={ele.productId} image={ele.img} name={ele.name} category={ele.category} qty={ele.qty} cost={ele.cost} rating={ele.rating} productId={ele.productId}/>
          })
        }
        <Box
          padding="1rem"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box color="#3C3C3C" alignSelf="center">
            Order total
          </Box>
          <Box
            color="#3C3C3C"
            fontWeight="700"
            fontSize="1.5rem"
            alignSelf="center"
            data-testid="cart-total"
          >
            ${total}
          </Box>
        </Box>
        {!isReadOnly && <Box display="flex" justifyContent="flex-end" className="cart-footer">
          <Button
            color="primary"
            variant="contained"
            startIcon={<ShoppingCart />}
            className="checkout-btn"
            onClick={() => {
              history.push('/checkout')
            }}
          >
            Checkout
          </Button>
        </Box>}
      </Box>
      {isReadOnly && <Box 
      padding="1rem"
      backgroundColor="white"
      margin="0.5rem"
      >
        <CartDetails />
      </Box>}
    </>
  );
};

export default Cart;
